import { useCallback, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAppState } from "../hooks/useAppState";
import { useAppSession } from "../hooks/useAppSession";
import { useAppFullscreen } from "../hooks/useAppFullscreen";
import { useAutoLock } from "../hooks/useAutoLock";
import { useBackgroundStyle } from "../hooks/useBackgroundStyle";
import { LockScreen } from "../components/ui/LockScreen";
import { LandingView } from "../components/landing/LandingView";
import { FitScreen } from "../components/landing/FitScreen";

export default function Landing() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAppSession();
  const { state, loading, error } = useAppState(isAuthenticated);
  const [locked, setLocked] = useState(false);
  const lock = useCallback(() => setLocked(true), []);
  // Hook ANTES de cualquier early return (regla §10 SYSTEM.md). Recibe
  // state?.global porque state es null mientras carga; el hook devuelve
  // undefined en ese caso y no se aplica fondo. Antes detectaba
  // orientación inline en cada render — si rotaban la TV, el fondo no
  // se refrescaba. El hook escucha resize + orientationchange.
  const bgStyle = useBackgroundStyle(state?.global);

  useAppFullscreen();
  useAutoLock(state?.settings.auto_bloqueo_minutos ?? 10, lock);

  // Sin sesión → redirect a setup. Usamos el componente <Navigate> en vez
  // de navigate() imperativo: llamar navigate() durante el render dispara
  // un update del router mientras este componente renderiza (warning
  // "Cannot update a component while rendering a different component" +
  // "call navigate() in useEffect"). <Navigate> hace el redirect en un
  // efecto, que es lo correcto.
  if (!isAuthenticated) {
    return <Navigate to="/setup" replace />;
  }

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin opacity-50" />
      </main>
    );
  }

  if (error || !state) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-red-400">No se pudo cargar el estado de la pantalla.</p>
        <p className="text-sm opacity-60">{error?.message}</p>
        <button
          onClick={() => navigate("/setup", { replace: true })}
          className="rounded-lg bg-white/10 px-4 py-2 hover:bg-white/20"
        >
          Volver al setup
        </button>
      </main>
    );
  }

  const { settings, campana, global, premios } = state;

  // El markup vive en LandingView (compartido con el preview del admin) y se
  // escala con FitScreen para verse igual a cualquier resolución (mismo lienzo
  // que el preview → coinciden 100%). El LockScreen (overlay interactivo) va
  // afuera, a pantalla completa.
  return (
    <>
      <FitScreen bgStyle={bgStyle}>
        <LandingView
          global={global}
          settings={settings}
          campana={campana}
          onLock={lock}
          ruletaSlots={premios.ruleta.length}
        />
      </FitScreen>
      <LockScreen open={locked} onUnlock={() => setLocked(false)} />
    </>
  );
}
